import { useCallback } from 'react';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import * as Sentry from '@sentry/react';
import { isApolloError } from '@apollo/client';

import { logInDev } from '../../lib/logger/logger';
import { useDeviceTokenRegisterMutation } from '../../graphql/types.generated';
import { getDeviceId, getDevicePlatform } from '../../lib/device/utils/info';
import { getDeviceType } from '../device/utils/info';
import { getPushNotificationsState, syncPushNotificationsState } from './usePushNotificationsState';
import { PushNotificationPermissionDeniedError } from './errors';

export const PUSH_NOTIFICATIONS_REGISTRATION_EXPIRATION_DAYS = 1;

export const getIsExpired = (registrationTimestamp: string | null) => {
  if (!registrationTimestamp) {
    return true;
  }

  const now = new Date();
  const expirationDate = new Date(registrationTimestamp);
  expirationDate.setDate(expirationDate.getDate() + PUSH_NOTIFICATIONS_REGISTRATION_EXPIRATION_DAYS);
  return expirationDate < now;
};

type GetIsChangedParamsCurrent = { deviceId: string; token: string };
type GetIsChangedParamsCached = { deviceId: string | null; token: string | null };

export const getIsChanged = (current: GetIsChangedParamsCurrent, cached: GetIsChangedParamsCached) => {
  return !(current.token === cached.token && current.deviceId === cached.deviceId);
};

// FCM - Firebase Cloud Messaging
const getFCMToken = (): Promise<{ token: string }> => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    // "try catch" used to handle any exceptions during registration
    // and reject promise that returned from getFCMToken
    try {
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        reject(new PushNotificationPermissionDeniedError());
        return;
      }

      const onRegistrationSuccess = (token: Token) => {
        resolve({ token: token.value });
        PushNotifications.removeAllListeners().catch(logInDev);
      };

      const onRegistrationFailed = (payload: any) => {
        reject(payload?.error || new Error('Push Notifications registration failed'));
        PushNotifications.removeAllListeners().catch(logInDev);
      };

      await PushNotifications.addListener('registration', onRegistrationSuccess);
      await PushNotifications.addListener('registrationError', onRegistrationFailed);

      // "await" used to handle exceptions during registration
      // in case if plugin is broken, not implemented, etc
      await PushNotifications.register();
    } catch (e) {
      reject(e);
      PushNotifications.removeAllListeners().catch(logInDev);
    }
  });
};

export const usePushNotifications = () => {
  const [registerDeviceToken] = useDeviceTokenRegisterMutation();

  const registerPushNotifications = useCallback(async () => {
    try {
      const devicePlatform = getDevicePlatform();
      // Do not use plugin/register token on web
      if (devicePlatform === 'web') {
        return;
      }

      const pushNotificationsState = await getPushNotificationsState();
      const { token } = await getFCMToken();
      const deviceId = await getDeviceId();
      const deviceType = getDeviceType();

      const isExpired = getIsExpired(pushNotificationsState.registrationTimestamp);
      const isChanged = getIsChanged(
        { deviceId, token },
        { deviceId: pushNotificationsState.deviceId, token: pushNotificationsState.fcmToken },
      );

      // Do not register token if device ID and token are not changed and not expired
      if (!isChanged && !isExpired) {
        return;
      }

      await registerDeviceToken({
        variables: {
          input: {
            token,
            deviceId,
            deviceType,
          },
        },
      });
      await syncPushNotificationsState({ deviceId, fcmToken: token });
    } catch (e) {
      logInDev(e);

      if (e instanceof PushNotificationPermissionDeniedError) {
        return;
      }

      if (e instanceof Error && !isApolloError(e)) {
        Sentry.captureException(e);
      }
    }
  }, [registerDeviceToken]);

  return { registerPushNotifications };
};
